import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { SuiContainer, SuiPopover, ORIGIN_BOTTOM_LEFT } from '@splashthat/sui';

import { filterMarkup } from '../../utils/filterMarkupHelper';

import './LayoutNodeLabel.styl';

const baseClassName = 'LayoutNodeLabel';
const BUTTON_TAG = 'button';
const DIV_TAG = 'div';
const REQUIRED_TITLE = 'Required';
const OPEN_DELAY = 750;
const CLOSE_TIMEOUT_DELAY = 400;
/**
 * Component representing the question name in the layout view.
 */
const LayoutNodeLabel = ({
    subtitle,
    isRequired,
    isFaded,
    isActive,
    children,
    onClick,
    onFocus,
    onBlur,
    qaId,
    renderLabelPopover,
    disabled,
}) => {
    const [popoverVisible, setPopoverVisible] = useState(false);
    const anchorEl = useRef(null);
    const openTimeout = useRef();
    const closeTimeout = useRef();

    const openPopOver = () => {
        if (closeTimeout.current) {
            clearTimeout(closeTimeout.current);
        }
        if (!popoverVisible) {
            setPopoverVisible(true);
        }
    };
    const closePopOver = () => {
        if (openTimeout.current) {
            clearTimeout(openTimeout.current);
        }

        if (popoverVisible) {
            setPopoverVisible(false);
        }
    };

    const handleLabelOnHoverOut = () => {
        if (openTimeout.current) {
            clearTimeout(openTimeout.current);
        }

        if (popoverVisible) {
            closeTimeout.current = setTimeout(() => closePopOver(), CLOSE_TIMEOUT_DELAY);
        }
    };

    const handleLabelOnHover = () => {
        if (closeTimeout.current) {
            clearTimeout(closeTimeout.current);
        }
        openTimeout.current = setTimeout(() => openPopOver(), OPEN_DELAY);
    };
    const isFocusable = !disabled && !!onClick;
    // Making it a <button> when it can be clicked, so that we can tab to it.
    const TagName = isFocusable ? BUTTON_TAG : DIV_TAG;
    const className = [
        baseClassName,
        isFaded && `${baseClassName}--faded`,
        !disabled && isActive && isFocusable && `${baseClassName}--active`,
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <SuiContainer
            data-qa={`${qaId}__editor__tab`}
            grow
            className={className}
            onMouseLeave={handleLabelOnHoverOut}
            onMouseEnter={handleLabelOnHover}
        >
            <TagName
                variation="text"
                className={isFocusable ? [`${baseClassName}__button`].filter(Boolean).join(' ') : ''}
                data-qa={`${baseClassName}__${TagName}-${children}`}
                ref={anchorEl}
                {...(!disabled && {
                    onClick,
                    onFocus,
                    onBlur,
                })}
            >
                {!children ? (
                    ''
                ) : (
                    <h3 className={`${baseClassName}__label`}>
                        <span className={`${baseClassName}__label-content`}>{filterMarkup(children)}</span>
                        {isRequired && (
                            <span className={`${baseClassName}__required`} title={REQUIRED_TITLE}>
                                *
                            </span>
                        )}
                    </h3>
                )}
                {subtitle && <h4 className={`${baseClassName}__type`}>{filterMarkup(subtitle)}</h4>}
            </TagName>
            {children && renderLabelPopover && (
                <SuiPopover
                    open={popoverVisible}
                    anchorEl={anchorEl.current}
                    anchorOrigin={ORIGIN_BOTTOM_LEFT}
                    useClickCatcher={false}
                    onClick={(e) => e.stopPropagation()}
                >
                    <SuiContainer variation="padded standard-shadow border">
                        <p>{filterMarkup(children)}</p>
                    </SuiContainer>
                </SuiPopover>
            )}
        </SuiContainer>
    );
};

LayoutNodeLabel.propTypes = {
    isRequired: PropTypes.bool.isRequired,
    isFaded: PropTypes.bool.isRequired,
    isActive: PropTypes.bool.isRequired,
    subtitle: PropTypes.string.isRequired,
    children: PropTypes.string.isRequired,
    /** optional - pass undefined if element shouldn't be clickable or focusable. */
    onClick: PropTypes.func,
    onFocus: PropTypes.func.isRequired,
    onBlur: PropTypes.func.isRequired,
    qaId: PropTypes.string,
    renderLabelPopover: PropTypes.bool,
    disabled: PropTypes.bool,
};

LayoutNodeLabel.defaultProps = {
    onClick: undefined,
    qaId: '',
    renderLabelPopover: false,
    disabled: false,
};

export default LayoutNodeLabel;
